import React, { useEffect, useRef } from 'react';
import { Lightbulb, Trash2, X } from 'lucide-react';

export interface BinContextMenuAction {
    id: 'suggest' | 'empty-bin';
    label: string;
    description?: string;
    icon: React.ReactNode;
}

interface BinContextMenuProps {
    x: number;
    y: number;
    binCode: string;
    onAction: (actionId: BinContextMenuAction['id']) => void;
    onClose: () => void;
}

const ACTIONS: BinContextMenuAction[] = [
    {
        id: 'suggest',
        label: 'Foreslå plassering',
        description: 'Få anbefalte destinasjoner fra regelmotoren',
        icon: <Lightbulb className="w-4 h-4 text-amber-500" />
    },
    {
        id: 'empty-bin',
        label: 'Tøm lokasjon',
        description: 'Flytt alt innhold til auto-foreslåtte destinasjoner',
        icon: <Trash2 className="w-4 h-4 text-red-500" />
    }
];

/**
 * Small right-click menu over a bin. Click outside or Escape closes.
 */
export const BinContextMenu: React.FC<BinContextMenuProps> = ({ x, y, binCode, onAction, onClose }) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
        };
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    // Clamp to viewport
    const style: React.CSSProperties = {
        position: 'fixed',
        left: Math.min(x, window.innerWidth - 260),
        top: Math.min(y, window.innerHeight - 180),
        zIndex: 60
    };

    return (
        <div
            ref={menuRef}
            style={style}
            className="bg-white rounded-lg shadow-xl border border-gray-200 w-64 py-1"
        >
            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                <div className="font-mono text-xs font-semibold text-gray-800 truncate">{binCode}</div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Lukk">
                    <X className="w-4 h-4" />
                </button>
            </div>
            {ACTIONS.map(action => (
                <button
                    key={action.id}
                    onClick={() => onAction(action.id)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-start space-x-2"
                >
                    <div className="flex-shrink-0 mt-0.5">{action.icon}</div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-800">{action.label}</div>
                        {action.description && (
                            <div className="text-xs text-gray-500">{action.description}</div>
                        )}
                    </div>
                </button>
            ))}
        </div>
    );
};
